<script setup>
import { ref, defineProps, defineEmits } from "vue"
import { useRoute } from "vue-router"
import { useStore } from "vuex"
import { notify } from "notiwind"
import { useI18n } from "vue-i18n"
import { useGtm } from "@gtm-support/vue-gtm"
import { EyeIcon, EyeSlashIcon } from "@heroicons/vue/24/outline"
import { CheckCircleIcon } from "@heroicons/vue/20/solid"
import { RadioGroup, RadioGroupLabel, RadioGroupOption } from "@headlessui/vue"

const props = defineProps({
  defaultType: { required: false, type: String, default: "" },
})
const route = useRoute()
const showPassword = ref(false)
const { t } = useI18n()
const gtm = useGtm()
const store = useStore()
const type = ref("householder")
const emit = defineEmits(["selectedType"])

const types = [
  { id: "householder", title: t("sign-in.type.householder") },
  { id: "installer", title: t("sign-in.type.installer") },
]

const gtmEvents = {
  householder: "authentication_user_sign_in",
  installer: "authentication_installer_sign_in",
}

const form = ref({
  email: "",
  password: "",
})
const loading = ref(false)
const error = ref()

async function login() {
  try {
    loading.value = true
    error.value = undefined

    const userType = props.defaultType ? props.defaultType : type.value

    await store.dispatch("auth/login", {
      email: form.value.email,
      password: form.value.password,
      type: userType,
    })

    gtm.trackEvent({
      event: gtmEvents[userType],
    })

    notify(
      {
        title: t("snackbars.success.title"),
        text: t("snackbars.login-success"),
        type: "info",
        group: "foo",
      },
      4000
    )

    if (props.defaultType) {
      if (props.defaultType === "installer") window.location = "/installers/"
      else if (props.defaultType === "householder")
        window.location = "/householders/"
    } else if (type.value === "installer") {
      if (
        route.query.redirectUrl &&
        new URL(route.query.redirectUrl).pathname.startsWith("/installers")
      )
        window.location = route.query.redirectUrl
      else window.location = "/installers/"
    } else if (type.value === "householder") {
      if (
        route.query.redirectUrl &&
        new URL(route.query.redirectUrl).pathname.startsWith("/householders")
      )
        window.location = route.query.redirectUrl
      else window.location = "/householders/"
    }
  } catch (exception) {
    if (
      exception.response.status == 422 &&
      exception.response.data.code == "account.locked"
    ) {
      error.value = "account-locked"
    }
    notify(
      {
        title: t("snackbars.errors.title"),
        text: t("snackbars.errors.login-failed"),
        type: "warning",
        group: "foo",
      },
      4000
    )
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <form @submit.prevent>
    <div v-if="!defaultType" class="flex flex-wrap -mx-3 mb-4">
      <div class="w-full px-3">
        <RadioGroup v-model="type">
          <RadioGroupLabel class="text-sm font-medium text-gray-800"
            >{{ $t("sign-in.type-select") }}
          </RadioGroupLabel>

          <div class="mt-4 grid grid-cols-1 gap-y-6 sm:grid-cols-2 sm:gap-x-4">
            <RadioGroupOption
              v-for="item in types"
              :key="item.id"
              v-slot="{ checked, active }"
              as="template"
              :value="item.id"
              @click="$emit('selectedType', item.id)"
            >
              <div
                :class="[
                  checked ? 'border-transparent' : 'border-gray-300',
                  active ? 'border-indigo-500 ring-2 ring-indigo-500' : '',
                  'relative flex cursor-pointer rounded-lg border bg-white p-4 shadow-sm focus:outline-none',
                ]"
              >
                <span class="flex flex-1">
                  <span class="flex flex-col">
                    <RadioGroupLabel
                      as="span"
                      class="block text-sm font-medium text-gray-900"
                      >{{ item.title }}
                    </RadioGroupLabel>
                  </span>
                </span>
                <CheckCircleIcon
                  :class="[
                    !checked ? 'invisible' : '',
                    'h-5 w-5 text-indigo-600',
                  ]"
                  aria-hidden="true"
                />
                <span
                  :class="[
                    active ? 'border' : 'border-2',
                    checked ? 'border-indigo-500' : 'border-transparent',
                    'pointer-events-none absolute -inset-px rounded-lg',
                  ]"
                  aria-hidden="true"
                />
              </div>
            </RadioGroupOption>
          </div>
        </RadioGroup>
      </div>
    </div>
    <div class="flex flex-wrap -mx-3 mb-4">
      <div class="w-full px-3">
        <label
          class="block text-gray-800 text-sm font-medium mb-1"
          for="email"
          >{{ $t("common.email") }}</label
        >
        <input
          id="email"
          v-model="form.email"
          type="email"
          class="form-input w-full text-gray-800"
          :placeholder="$t('common.enter-your-email')"
          required
        />
      </div>
    </div>
    <div class="flex flex-wrap -mx-3 mb-4">
      <div class="w-full px-3">
        <div class="flex justify-between">
          <label
            class="block text-gray-800 text-sm font-medium mb-1"
            for="password"
            >{{ $t("common.password") }}</label
          >
          <router-link
            to="/reset-password"
            class="text-sm font-medium text-blue-600 hover:underline"
            >{{ $t("sign-in.having-trouble") }}</router-link
          >
        </div>
        <div class="relative">
          <input
            id="password"
            v-model="form.password"
            :type="!showPassword ? 'password' : 'text'"
            class="form-input w-full text-gray-800"
            :placeholder="$t('common.enter-your-password')"
            required
          />
          <button
            type="button"
            class="absolute top-1/2 right-3 transform -translate-y-1/2 text-gray-500"
            aria-label="Toggle password visibility"
            @click="showPassword = !showPassword"
          >
            <template v-if="showPassword">
              <EyeSlashIcon class="w-5 h-5" />
            </template>
            <template v-else>
              <EyeIcon class="w-5 h-5" />
            </template>
          </button>
        </div>
      </div>
    </div>
    <div class="flex flex-wrap -mx-3 mb-4">
      <div class="w-full px-3">
        <div class="flex justify-between">
          <label class="flex items-center">
            <input type="checkbox" class="form-checkbox" />
            <span class="text-gray-600 ml-2">{{
              $t("sign-in.keep-me-signed-in")
            }}</span>
          </label>
        </div>
      </div>
    </div>
    <div class="flex flex-wrap -mx-3 mt-6">
      <div class="w-full px-3">
        <button
          type="submit"
          class="btn text-white bg-black hover:bg-grey w-full"
          @click="login"
        >
          <template v-if="loading">
            <svg
              class="mr-3 h-5 w-5 animate-spin text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              ></circle>
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            {{ $t("common.processing") }}...
          </template>
          <span v-else>
            {{ $t("common.sign-in") }}
          </span>
        </button>
      </div>
    </div>
    <p v-if="error" class="mt-2 text-sm text-red-600">
      {{ $t(`sign-in.errors.${error}`) }}
    </p>
  </form>
</template>
